import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import noop from 'lib/function/noop'
import type {
  FocusSurfaceContentActionData,
  AssistantSurfaceContentActionSnapshot,
} from 'tripPlanner/api/types'
import type {
  AssistantSurfaceTargetRegistration,
  SurfaceFocusTargetRegistrationInput,
} from 'tripPlanner/assistant/surfaceFocus/surfaceFocusTypes'
import type { SurfaceSnapshotAuthorityFields } from 'tripPlanner/assistant/surfaceFocus/resolveFocusAction'
import type {
  OfferPageAssistantSource,
  OfferPageSnapshotContribution,
  OfferPageSnapshotInput,
} from './offerPageSnapshotTypes'
import { buildOfferPageSnapshotScopeKeyFromSource } from './offerPageSnapshotIdentity'

/** A streamed focus action plus the live snapshot authority it must validate against. */
export interface OfferPageFocusActionDispatch {
  action: FocusSurfaceContentActionData
  preSnapshot: AssistantSurfaceContentActionSnapshot
  liveAuthority: SurfaceSnapshotAuthorityFields | undefined
}

export type OfferPageFocusActionHandler = (dispatch: OfferPageFocusActionDispatch) => void

interface OfferPageAssistantSurfaceRegistryValue {
  getCurrentSnapshotInput: () => OfferPageSnapshotInput | undefined
  registerContribution: (contribution: OfferPageSnapshotContribution) => () => void
  registerSource: (source: OfferPageAssistantSource) => () => void
  /** Registers a mounted focus target; `surface`/`surfaceContextScopeKey` are stamped on read. */
  registerFocusTarget: (targetId: string, registration: SurfaceFocusTargetRegistrationInput) => () => void
  getFocusTarget: (targetId: string) => AssistantSurfaceTargetRegistration | undefined
  /** Registers the single executor handler; dispatch forwards the paired action + authority to it. */
  setFocusActionHandler: (handler: OfferPageFocusActionHandler) => () => void
  dispatchFocusAction: (dispatch: OfferPageFocusActionDispatch) => void
}

const OfferPageAssistantSurfaceRegistryContext =
  createContext<OfferPageAssistantSurfaceRegistryValue | undefined>(undefined)

interface Props {
  children: ReactNode
}

function sourceIdentity(source: OfferPageAssistantSource): string {
  return `${source.variant}:${source.key}`
}

function contributionIdentity(contribution: OfferPageSnapshotContribution): string {
  return `${contribution.sourcePath}:${contribution.key}`
}

export function useOfferPageAssistantSurfaceRegistry() {
  const sourcesRef = useRef(new Map<string, OfferPageAssistantSource>())
  const contributionsRef = useRef(new Map<string, OfferPageSnapshotContribution>())
  const focusTargetsRef = useRef(new Map<string, SurfaceFocusTargetRegistrationInput>())
  const focusActionHandlerRef = useRef<OfferPageFocusActionHandler | undefined>(undefined)

  const registerSource = useCallback((source: OfferPageAssistantSource) => {
    const identity = sourceIdentity(source)
    if (sourcesRef.current.get(identity) === source) {
      return noop
    }

    sourcesRef.current.set(identity, source)

    return () => {
      if (sourcesRef.current.get(identity) !== source) {
        return
      }

      sourcesRef.current.delete(identity)
    }
  }, [])

  const registerContribution = useCallback((contribution: OfferPageSnapshotContribution) => {
    const identity = contributionIdentity(contribution)
    if (contributionsRef.current.get(identity) === contribution) {
      return noop
    }

    contributionsRef.current.set(identity, contribution)

    return () => {
      if (contributionsRef.current.get(identity) !== contribution) {
        return
      }

      contributionsRef.current.delete(identity)
    }
  }, [])

  const getCurrentSource = useCallback((): OfferPageAssistantSource | undefined => {
    const sources = [...sourcesRef.current.values()]
    return sources.length === 1 ? sources[0] : undefined
  }, [])

  const getCurrentSnapshotInput = useCallback((): OfferPageSnapshotInput | undefined => {
    const source = getCurrentSource()
    if (!source) {
      return undefined
    }

    const contributions = [...contributionsRef.current.values()]
      .sort((left, right) => left.sourcePath.localeCompare(right.sourcePath) || left.key.localeCompare(right.key))

    return {
      source,
      contributions,
    }
  }, [getCurrentSource])

  const registerFocusTarget = useCallback((targetId: string, registration: SurfaceFocusTargetRegistrationInput) => {
    focusTargetsRef.current.set(targetId, registration)

    return () => {
      if (focusTargetsRef.current.get(targetId) !== registration) {
        return
      }

      focusTargetsRef.current.delete(targetId)
    }
  }, [])

  const getFocusTarget = useCallback((targetId: string): AssistantSurfaceTargetRegistration | undefined => {
    const registration = focusTargetsRef.current.get(targetId)
    if (!registration) {
      return undefined
    }

    const source = getCurrentSource()

    return {
      ...registration,
      targetId,
      surface: 'accommodation_offer_page',
      // Scope key is stamped from the live source at read time (not at registration) so it is
      // always current; the executor validates the streamed action against the live snapshot
      // authority separately, so a mounted registration always belongs to the current scope.
      surfaceContextScopeKey: (source ? buildOfferPageSnapshotScopeKeyFromSource(source) : undefined) ?? '',
    }
  }, [getCurrentSource])

  const setFocusActionHandler = useCallback((handler: OfferPageFocusActionHandler) => {
    focusActionHandlerRef.current = handler

    return () => {
      if (focusActionHandlerRef.current === handler) {
        focusActionHandlerRef.current = undefined
      }
    }
  }, [])

  const dispatchFocusAction = useCallback((dispatch: OfferPageFocusActionDispatch) => {
    focusActionHandlerRef.current?.(dispatch)
  }, [])

  return useMemo(() => ({
    getCurrentSnapshotInput,
    registerContribution,
    registerSource,
    registerFocusTarget,
    getFocusTarget,
    setFocusActionHandler,
    dispatchFocusAction,
  }), [
    getCurrentSnapshotInput,
    registerContribution,
    registerSource,
    registerFocusTarget,
    getFocusTarget,
    setFocusActionHandler,
    dispatchFocusAction,
  ])
}

export function OfferPageAssistantSurfaceProvider({ children }: Props) {
  const registry = useOfferPageAssistantSurfaceRegistry()

  return (
    <OfferPageAssistantSurfaceRegistryContext.Provider value={registry}>
      {children}
    </OfferPageAssistantSurfaceRegistryContext.Provider>
  )
}

export function useOfferPageAssistantSurfaceRegistryContext(): OfferPageAssistantSurfaceRegistryValue {
  const context = useContext(OfferPageAssistantSurfaceRegistryContext)
  if (!context) {
    throw new Error('useOfferPageAssistantSurfaceRegistryContext must be used within OfferPageAssistantSurfaceProvider')
  }

  return context
}

export function useOptionalOfferPageAssistantSurfaceRegistryContext(): OfferPageAssistantSurfaceRegistryValue | undefined {
  return useContext(OfferPageAssistantSurfaceRegistryContext)
}

interface OfferPageAssistantSourceRegistrationProps {
  source: OfferPageAssistantSource
}

export function OfferPageAssistantSourceRegistration({ source }: OfferPageAssistantSourceRegistrationProps) {
  const registry = useOptionalOfferPageAssistantSurfaceRegistryContext()

  useIsomorphicLayoutEffect(() => registry?.registerSource(source), [registry, source])

  return null
}

interface OfferPageSnapshotContributionRegistrationProps {
  contribution: OfferPageSnapshotContribution
}

export function OfferPageSnapshotContributionRegistration({
  contribution,
}: OfferPageSnapshotContributionRegistrationProps) {
  const registry = useOptionalOfferPageAssistantSurfaceRegistryContext()

  useIsomorphicLayoutEffect(() => registry?.registerContribution(contribution), [contribution, registry])

  return null
}
